import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ListPagination } from "@/components/list-pagination";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TOP_UPS_PER_PAGE, type TopUpFilters as Filters } from "@/features/billing/api";
import { topUpMethodsQuery, topUpsQuery } from "@/features/billing/queries";
import { readTopUpsSearch } from "@/features/billing/search";
import { TopUpFilters } from "@/features/billing/top-up-filters";
import { TopUpsTable } from "@/features/billing/top-ups-table";
import { m } from "@/lib/paraglide/messages.js";

// /billing/top-ups: the reseller's top-ups, newest first (docs/plans/billing-top-up.md §7), by status
// and by WIB calendar days. The filters and the page are route state (guide, rule 16); with none,
// every top-up of every day. Where the gateway sends the reseller back after paying.
export const Route = createFileRoute("/_authenticated/billing/top-ups")({
  validateSearch: (raw: Record<string, unknown>) => readTopUpsSearch(raw),
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    filters: { state: search.state, from: search.from, to: search.to } satisfies Filters,
  }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(topUpsQuery(deps.page, deps.filters)),
  staticData: { crumb: m.billing_tab_top_ups },
  component: TopUpsPage,
});

function TopUpsPage() {
  const { page: asked = 1, state, from, to } = Route.useSearch();
  const navigate = Route.useNavigate();
  const filters: Filters = { state, from, to };
  const { data: topUps } = useSuspenseQuery(topUpsQuery(asked, filters));
  const { data: methods } = useSuspenseQuery(topUpMethodsQuery());
  const { page, total } = topUps.meta;
  const pages = Math.max(1, Math.ceil(total / TOP_UPS_PER_PAGE));
  const shown = topUps.data.length;
  const first = shown === 0 ? 0 : (page - 1) * TOP_UPS_PER_PAGE + 1;
  const range = m.common_page_range({ from: first, to: first + shown - (shown ? 1 : 0), total });
  const names = Object.fromEntries(methods.channels.map((c) => [c.code, c.name]));
  // A changed filter starts again from the first page. Replaced, not pushed: Back leaves the page
  // rather than undoing each change.
  const onFiltersChange = (next: Filters) =>
    navigate({ search: (prev) => ({ ...prev, ...next, page: undefined }), replace: true });
  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.billing_tab_top_ups()}</CardTitle>
        <CardDescription>{m.billing_top_ups_description()}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <TopUpFilters filters={filters} onChange={onFiltersChange} />
        <TopUpsTable
          topUps={topUps.data}
          channelNames={names}
          caption={range}
          filtered={Boolean(state || from || to)}
        />
        <ListPagination page={page} pages={pages} />
      </CardContent>
    </Card>
  );
}
